import { RefObject, useCallback, useEffect, useLayoutEffect, useRef } from "react";

type Params = {
  containerRef: RefObject<HTMLDivElement>;
  onLoadMore: () => void;
  disabled?: boolean;
  threshold?: number;
};

export function useInfiniteScrollUp({
  containerRef,
  onLoadMore,
  disabled = false,
  threshold = 0,
}: Params) {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const prevScrollHeightRef = useRef(0);
  const disabledRef = useRef(disabled);
  disabledRef.current = disabled;

  // コンテンツが上に追加された後、スクロール位置を復元してジャンプを防ぐ
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container || prevScrollHeightRef.current === 0) return;

    const diff = container.scrollHeight - prevScrollHeightRef.current;
    if (diff > 0) {
      container.scrollTop += diff;
      prevScrollHeightRef.current = 0;
    }
  });

  const handleIntersect = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const entry = entries[0];
      if (!entry.isIntersecting || disabledRef.current) return;

      const container = containerRef.current;
      if (container) {
        prevScrollHeightRef.current = container.scrollHeight;
      }
      onLoadMore();
    },
    [containerRef, onLoadMore],
  );

  useEffect(() => {
    const sentinel = sentinelRef.current;
    const container = containerRef.current;
    if (!sentinel || !container) return;

    const observer = new IntersectionObserver(handleIntersect, {
      root: container,
      rootMargin: "200px 0px 0px 0px",
      threshold,
    });

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [containerRef, handleIntersect, threshold]);

  return { sentinelRef };
}
